import React, { useCallback, useEffect, useState } from "react";
import { isEqual } from "lodash";
import { connect } from "react-redux";

import { useStateSelector, resolveObj } from "utils/field";
import { upgradeOptionsComponents, upgradeOptionsSystems } from "utils/upgrades";

import { initForm, resetForm } from "store/form/actions";
import { actions } from "../_ducks";
import { actions as commUpgradesActions } from "features/Community/Upgrades/_ducks";
import { selectUpgrade } from "store/upgradeLibrary/actions";

import Field from "components/Field";
import Checkbox from "components/Input/Checkbox";
import Button from "components/Button";
import Drawer from "components/Drawer";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

const { updateUpgradePackage } = actions;
const { updateCommUpgradePackage } = commUpgradesActions;

const windowDirectionMap = {
    0: { key: "south", label: "South" },
    1: { key: "southEast", label: "Southeast" },
    2: { key: "east", label: "East" },
    3: { key: "northEast", label: "Northeast" },
    4: { key: "north", label: "North" },
    5: { key: "northWest", label: "Northwest" },
    6: { key: "west", label: "West" },
    7: { key: "southWest", label: "Southwest" },
};

// newForm.packageEditDrawer

// stable fallback: a fresh {} literal would change the initForm effect's deps every render
const EMPTY_PACKAGE = {};

const PriorityCostItem = ({ type, label, windowDirections }) => {
    if (type === "heatingCooling") {
        return (
            <>
                <InputRow className={classes.group} gridTemplate="1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        name={`newForm.packageEditDrawer.upgradeSettings.heating.cost`}
                        label={`Heating Cost`}
                        placeholder={0.0}
                        decimals={2}
                        units={{
                            base: "$",
                            selected: "$",
                            unitType: "cost",
                        }}
                    />
                    <Field
                        component={Input}
                        type="number"
                        name={`newForm.packageEditDrawer.upgradeSettings.heating.priority`}
                        label={`Heating Priority`}
                        placeholder={0}
                        decimals={0}
                        quantity
                    />
                </InputRow>
                <InputRow className={classes.group} gridTemplate="1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        name={`newForm.packageEditDrawer.upgradeSettings.cooling.cost`}
                        label={`Cooling Cost`}
                        placeholder={0.0}
                        decimals={2}
                        units={{
                            base: "$",
                            selected: "$",
                            unitType: "cost",
                        }}
                    />
                    <Field
                        component={Input}
                        type="number"
                        name={`newForm.packageEditDrawer.upgradeSettings.cooling.priority`}
                        label={`Cooling Priority`}
                        placeholder={0}
                        decimals={0}
                        quantity
                    />
                </InputRow>
            </>
        );
    } else if (type === "window") {
        return (
            <div className={classes.group}>
                <InputRow gridTemplate="1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        name={`newForm.packageEditDrawer.upgradeSettings.window.overall.cost`}
                        label={`${label} Cost`}
                        placeholder={0.0}
                        decimals={2}
                        units={{
                            base: "$",
                            selected: "$",
                            unitType: "cost",
                        }}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        type="number"
                        name={`newForm.packageEditDrawer.upgradeSettings.window.overall.priority`}
                        label={`${label} Priority`}
                        placeholder={0}
                        decimals={0}
                        quantity
                    />
                </InputRow>
                {windowDirections
                    .filter((dirId) => windowDirectionMap[dirId])
                    .sort()
                    .map((dirId, ind) => {
                        const direction = windowDirectionMap[dirId];
                        if (!direction) return null;

                        return (
                            <InputRow gridTemplate="1fr 1fr" key={ind}>
                                <Field
                                    component={InputWithUnits}
                                    name={`newForm.packageEditDrawer.upgradeSettings.window.cost.${direction.key}`}
                                    label={`${direction.label} Window Cost`}
                                    placeholder={0.0}
                                    decimals={2}
                                    units={{
                                        base: "$",
                                        selected: "$",
                                        unitType: "cost",
                                    }}
                                />
                            </InputRow>
                        );
                    })}
            </div>
        );
    }

    return (
        <InputRow className={classes.group} gridTemplate="1fr 1fr">
            <Field
                component={InputWithUnits}
                name={`newForm.packageEditDrawer.upgradeSettings.${type}.cost`}
                label={`${label} Cost`}
                placeholder={0.0}
                decimals={2}
                units={{
                    base: "$",
                    selected: "$",
                    unitType: "cost",
                }}
            />
            <Field
                component={Input}
                type="number"
                name={`newForm.packageEditDrawer.upgradeSettings.${type}.priority`}
                label={`${label} Priority`}
                placeholder={0}
                decimals={0}
                quantity
            />
        </InputRow>
    );
};

const PackageEditDrawer = React.memo(
    ({
        isOpen = false,
        toggleOpen,
        invalid,
        reset,
        selectUpgrade,
        selectedPackage = "",
        initForm,
        resetForm,
        isCommunity,
        updateCommUpgradePackage,
        updateUpgradePackage,
        modelsDefaultCodes,
    }) => {
        const [submitting, setSubmitting] = useState(false);

        const { packages = {} } = useStateSelector("upgrades") || {};
        const { packages: commPackages = ({} = {}) } = useStateSelector("commUpgrades") || {};
        const components = useStateSelector("model.modelData.components");

        const { major: h2kMajor, minor: h2kMinor } = useStateSelector("model.modelData.h2kVersion") || {};

        const initialValues = packages[selectedPackage] || commPackages[selectedPackage] || EMPTY_PACKAGE;
        const { upgrades = {} } = initialValues;

        useEffect(() => {
            if (!isOpen) return;

            initForm("packageEditDrawer", initialValues);
        }, [isOpen, initForm, initialValues]);

        const windowDirections = [
            ...new Set(
                Object.values(upgrades).reduce((cache, curr) => {
                    if (curr.upgradeType === "window") {
                        return [
                            ...cache,
                            ...curr.selectedComponents.reduce((cache2, windowAccessor) => {
                                return [...cache2, resolveObj(`${windowAccessor}.facingDirection.id`, components)];
                            }, []),
                        ];
                    }

                    return cache;
                }, [])
            ),
        ];

        const priorityOptions = Object.keys(upgrades).reduce((array, upgradeId) => {
            let { upgradeType = "" } = upgrades[upgradeId];

            if (upgradeType === "ceiling") {
                const { fields: { constructionType: { id: ceilingConstId = 0 } = {} } = {} } = upgrades[upgradeId];

                upgradeType = [2, 3].includes(ceilingConstId) ? "ceilingFlat" : "ceiling";
            }

            if (array.includes(upgradeType)) {
                return array;
            }

            return [...array, upgradeType];
        }, []);

        const allUpgrades = [
            ...upgradeOptionsComponents,
            { label: "Flat Ceiling", value: "ceilingFlat" },
            ...upgradeOptionsSystems,
        ];

        const handleClose = useCallback(() => {
            toggleOpen({});

            selectUpgrade(null);

            if (reset) {
                reset();
            }

            setTimeout(() => resetForm("packageEditDrawer"), 500);
        }, [toggleOpen, reset]);

        const isBase = selectedPackage === "base";

        const checkBoxSectionLabel = [9, 10, 11, 12].includes(h2kMinor) ? "Greener Homes" : "Resiliency Measures";
        const checkBoxSectionAccessor = [9, 10, 11, 12].includes(h2kMinor) ? "greenerHomes" : "resiliencyMeasures";

        const { upgradeSettings = {}, name = "" } = useStateSelector("newForm.packageEditDrawer") || {};

        const handleSubmit = async () => {
            setSubmitting(true);

            if (isCommunity) {
                await updateCommUpgradePackage({
                    packageId: selectedPackage,
                    packageUpdates: { upgradeSettings, name },
                });
            } else {
                await updateUpgradePackage({
                    packageId: selectedPackage,
                    packageUpdates: { upgradeSettings, name },
                });
            }

            toggleOpen({});
            selectUpgrade(null);

            if (reset) {
                reset();
            }

            setTimeout(() => resetForm("packageEditDrawer"), 500);

            setSubmitting(false);
        };

        return (
            <Drawer
                open={isOpen}
                title="Edit Package"
                subtitle={
                    isBase ? "Change your base house label." : "Change your package name and define upgrade priorities."
                }
                close={handleClose}
                classes={{
                    paperClass: classes.packageDrawerPaper,
                }}
            >
                <div className={classes.contentWrapper}>
                    <div className={classes.mainContent}>
                        <InputRow>
                            <Field
                                component={Input}
                                name={"newForm.packageEditDrawer.name"}
                                label={`Package Name`}
                                placeholder="Name your package"
                                // validate={[required]}
                            />
                        </InputRow>
                        {!isBase && (
                            <>
                                <h3>Energy Upgrade Priorities</h3>
                                <div>
                                    {priorityOptions
                                        .sort((a, b) => {
                                            const isASystem = upgradeOptionsSystems.find(({ value }) => value === a);
                                            const isBSystem = upgradeOptionsSystems.find(({ value }) => value === b);

                                            if (isBSystem && !isASystem) {
                                                return -1;
                                            }

                                            if (!isBSystem && isASystem) {
                                                return 1;
                                            }

                                            return a > b ? 1 : a < b ? -1 : 0;
                                        })
                                        .map((type) => {
                                            const label = allUpgrades.find(({ value }) => value === type)?.label;

                                            return (
                                                <PriorityCostItem
                                                    key={`${type}-priority-cost`}
                                                    type={type}
                                                    label={label}
                                                    windowDirections={windowDirections}
                                                />
                                            );
                                        })}
                                </div>
                                {h2kMinor >= 10 && (
                                    <>
                                        <h3>{checkBoxSectionLabel}</h3>
                                        <InputRow gridTemplate="1fr 1fr">
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.basementSlabInsulated`}
                                                label="Basement slab insulated"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.backwaterValve`}
                                                label="Backwater valve with alarm"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                        </InputRow>
                                        <InputRow gridTemplate="1fr 1fr">
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.moistureProofCrawlSpace`}
                                                label="Moisture proof crawl space"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.sumpPump`}
                                                label="Sump pump with alarm and battery backup"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                        </InputRow>
                                        <InputRow gridTemplate="1fr 1fr">
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.waterproofing`}
                                                label="Waterproofing"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.smartThermostats`}
                                                label="Smart thermostats"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                        </InputRow>
                                        <InputRow gridTemplate="1fr 1fr">
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.roofingMembrane`}
                                                label="Adhesive waterproof ice and water barrier underlayment for roofs"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                            <Field
                                                component={Checkbox}
                                                name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.minR10ContinuousExposedFloors`}
                                                label="Min. R10 continuous for exposed floors"
                                                large
                                                className={classes.bottomCheckBox}
                                                type="checkbox"
                                            />
                                        </InputRow>
                                        {h2kMinor >= 13 && (
                                            <InputRow gridTemplate="1fr 1fr">
                                                <Field
                                                    component={Checkbox}
                                                    name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.evCharger`}
                                                    label="EV Charger"
                                                    large
                                                    className={classes.bottomCheckBox}
                                                    type="checkbox"
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    name={`newForm.packageEditDrawer.upgradeSettings.${checkBoxSectionAccessor}.elecPanelUpgraded`}
                                                    label="Electrical Panel Upgrade"
                                                    large
                                                    className={classes.bottomCheckBox}
                                                    type="checkbox"
                                                />
                                            </InputRow>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${globalStyles.buttons} ${classes.drawerButtons}`}>
                        <Button large type="hollow" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button large onClick={handleSubmit} disabled={submitting || invalid}>
                            {submitting ? "Applying Changes..." : "Apply Changes"}
                        </Button>
                    </div>
                </div>
            </Drawer>
        );
    },
    isEqual
);

const mapDispatchToProps = (dispatch) => ({
    selectUpgrade: (upgradeId) => dispatch(selectUpgrade(upgradeId)),
    initForm: (formId, values) => dispatch(initForm(formId, values)),
    resetForm: (formId) => dispatch(resetForm(formId)),
    updateCommUpgradePackage: ({ packageId, packageUpdates }) =>
        dispatch(updateCommUpgradePackage({ packageId, packageUpdates })),
    updateUpgradePackage: ({ packageId, packageUpdates }) =>
        dispatch(updateUpgradePackage({ packageId, packageUpdates })),
});

export default connect(null, mapDispatchToProps)(PackageEditDrawer);
